// Capacitor 앱 전용 선택 업데이트 안내.
// 웹/PWA에서는 네이티브 버전 API와 원격 버전 파일을 호출하지 않는다.

import { useEffect, useState } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { isNativeApp } from "@/shared/platform.ts";
import { Button } from "@/shared/ui";

const VERSION_MANIFEST_URL =
  (import.meta.env.VITE_NATIVE_VERSION_MANIFEST_URL as string | undefined) ??
  "https://openchuksan.com/native-app-version.json";
const UPDATE_REDIRECT_URL =
  (import.meta.env.VITE_NATIVE_UPDATE_URL as string | undefined) ??
  "https://openchuksan.com/download";

interface UpdateInfo {
  currentVersion: string;
  latestVersion: string;
}

// 점으로 구분된 Android versionName을 숫자 단위로 비교한다(예: 1.0.9 < 1.0.12).
export function isVersionOlder(current: string, latest: string): boolean {
  const parse = (value: string) =>
    value
      .trim()
      .replace(/^v/iu, "")
      .split(".")
      .map((part) => Number(part.match(/^\d+/u)?.[0] ?? 0));
  const currentParts = parse(current);
  const latestParts = parse(latest);
  const length = Math.max(currentParts.length, latestParts.length);
  for (let index = 0; index < length; index += 1) {
    const currentPart = currentParts[index] ?? 0;
    const latestPart = latestParts[index] ?? 0;
    if (currentPart < latestPart) return true;
    if (currentPart > latestPart) return false;
  }
  return false;
}

// 앱 시작 시 한 번만 최신 버전을 확인하고, 실패해도 사용을 막지 않는 선택 업데이트 모달을 관리한다.
export default function NativeUpdatePrompt() {
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [openingStore, setOpeningStore] = useState(false);

  useEffect(() => {
    if (!isNativeApp) return;
    const controller = new AbortController();

    void (async () => {
      try {
        const cacheBuster = VERSION_MANIFEST_URL.includes("?") ? "&" : "?";
        const [appInfo, response] = await Promise.all([
          CapacitorApp.getInfo(),
          fetch(`${VERSION_MANIFEST_URL}${cacheBuster}t=${Date.now()}`, {
            cache: "no-store",
            signal: controller.signal,
          }),
        ]);
        if (!response.ok) return;
        const manifest = (await response.json()) as { latestVersion?: unknown };
        if (typeof manifest.latestVersion !== "string") return;
        if (isVersionOlder(appInfo.version, manifest.latestVersion)) {
          setUpdateInfo({
            currentVersion: appInfo.version,
            latestVersion: manifest.latestVersion,
          });
        }
      } catch {
        // 버전 확인 실패는 앱 사용을 막지 않는다. 다음 실행 때 다시 확인한다.
      }
    })();

    return () => controller.abort();
  }, []);

  if (!updateInfo) return null;

  async function openUpdatePage() {
    setOpeningStore(true);
    try {
      // 배포 QR과 같은 리다이렉트 주소를 열어 Android는 Play 스토어 경로로 보낸다.
      await Browser.open({ url: UPDATE_REDIRECT_URL });
    } finally {
      setOpeningStore(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-ink-900/50 p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="native-update-title"
        className="w-full max-w-sm rounded-2xl bg-surface p-5 shadow-elevated"
      >
        <h2 id="native-update-title" className="text-xl font-bold text-ink-900">
          새로운 버전이 있습니다
        </h2>
        <p className="mt-2 text-sm leading-6 text-ink-700">
          더 안정적인 이용을 위해 최신 버전으로 업데이트해 주세요. 지금 업데이트하지 않아도 앱은
          계속 사용할 수 있습니다.
        </p>
        <p className="mt-2 text-xs text-ink-500">
          현재 {updateInfo.currentVersion} · 최신 {updateInfo.latestVersion}
        </p>
        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={() => setUpdateInfo(null)}
            className="inline-flex min-h-touch flex-1 items-center justify-center rounded-md border border-ink-300 bg-surface px-4 text-sm font-semibold text-ink-800 hover:bg-ink-50"
          >
            나중에
          </button>
          <Button
            type="button"
            fullWidth
            onClick={openUpdatePage}
            loading={openingStore}
            disabled={openingStore}
          >
            업데이트
          </Button>
        </div>
      </section>
    </div>
  );
}
